import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError, OpponentRow } from '../api';
import { opponentEndpoints } from '../api';
import { NotPermitted } from '../App';
import { usePermissions } from '../session';
import { Badge, Button, ErrorNote, Field, Panel, formatWhen } from '../ui';
import { INPUT } from './event/shared';

/**
 * Opponents (D-368): the teams home games are played against.
 *
 * One event per game, so a season is seventy events against a dozen teams. A
 * team is kept here once, with its logo, and each game picks it on its
 * Overview tab — so the logo is uploaded once and every game against that team
 * shows it. A team that leaves the league is archived: off the game picker,
 * still named by the games already played against it.
 */
export default function OpponentsPage() {
  const can = usePermissions();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const opponents = useQuery({ queryKey: ['opponents', 'all'], queryFn: () => opponentEndpoints.list(true) });
  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['opponents'] });
  };
  const onError = (cause: ApiError) => setError(cause.message);

  if (!can('events.view')) return <NotPermitted permission="events.view" />;
  const editable = can('events.manage');
  const all = opponents.data?.opponents ?? [];
  const archivedCount = all.filter((row) => row.archived).length;
  const rows = showArchived ? all : all.filter((row) => !row.archived);

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title="Opponents"
        description="The teams home games are played against. Each game picks its opponent on its Overview tab, and the schedule shows the team's name and logo. A game with no opponent is listed as another event at the ballpark."
        actions={
          <div className="flex items-center gap-3">
            {archivedCount > 0 ? (
              <label className="flex items-center gap-2 text-sm text-ink-600">
                <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
                Show archived ({archivedCount})
              </label>
            ) : null}
            {editable && !adding ? (
              <Button variant="primary" onClick={() => setAdding(true)}>
                New opponent
              </Button>
            ) : null}
          </div>
        }
      >
        {adding ? (
          <OpponentForm
            opponent={null}
            onDone={() => {
              setAdding(false);
              refresh();
            }}
            onCancel={() => setAdding(false)}
            onError={onError}
          />
        ) : null}
        {opponents.isLoading ? <p className="px-5 py-4 text-sm text-ink-500">Loading…</p> : null}
        {!opponents.isLoading && rows.length === 0 && !adding ? <p className="px-5 py-4 text-sm text-ink-500">No opponents yet.</p> : null}
        <ul className="divide-y divide-line">
          {rows.map((row) => (
            <OpponentItem key={row.id} opponent={row} editable={editable} onSaved={refresh} onError={onError} />
          ))}
        </ul>
      </Panel>
    </div>
  );
}

function OpponentItem({ opponent, editable, onSaved, onError }: { opponent: OpponentRow; editable: boolean; onSaved: () => void; onError: (cause: ApiError) => void }) {
  const [editing, setEditing] = useState(false);
  const archive = useMutation({
    mutationFn: () => (opponent.archived ? opponentEndpoints.restore(opponent.id) : opponentEndpoints.archive(opponent.id)),
    onSuccess: onSaved,
    onError,
  });
  const upload = useMutation({ mutationFn: (file: File) => opponentEndpoints.uploadLogo(opponent.id, file), onSuccess: onSaved, onError });
  const removeLogo = useMutation({ mutationFn: () => opponentEndpoints.removeLogo(opponent.id), onSuccess: onSaved, onError });
  const pick = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/png,image/jpeg,image/webp';
    input.onchange = () => {
      const file = input.files?.[0];
      if (file) upload.mutate(file);
    };
    input.click();
  };

  if (editing) {
    return (
      <li>
        <OpponentForm
          opponent={opponent}
          onDone={() => {
            setEditing(false);
            onSaved();
          }}
          onCancel={() => setEditing(false)}
          onError={onError}
        />
      </li>
    );
  }

  return (
    <li className="flex flex-wrap items-center gap-4 px-5 py-4">
      <div className="flex size-16 shrink-0 items-center justify-center rounded border border-line bg-paper">
        {opponent.admin_logo_url ? (
          <img src={opponent.admin_logo_url} alt={`${opponent.name} logo`} className="max-h-14 max-w-14 object-contain" />
        ) : (
          <span className="text-sm font-semibold text-ink-400">{opponent.abbreviation ?? '—'}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-ink">
          {opponent.name} {opponent.archived ? <Badge>Archived</Badge> : null}
        </p>
        <p className="text-sm text-ink-600">
          {[opponent.short_name, opponent.abbreviation].filter(Boolean).join(' · ') || 'No short name or abbreviation'}
        </p>
        <p className="text-xs text-ink-400">
          {opponent.games === 1 ? '1 game' : `${opponent.games} games`}
          {opponent.next_game_at ? `, next ${formatWhen(opponent.next_game_at)}` : ''}
          <span className="ml-2 font-mono">{opponent.slug}</span>
        </p>
      </div>
      {editable ? (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setEditing(true)}>Edit</Button>
          <Button onClick={pick} disabled={upload.isPending}>
            {upload.isPending ? 'Uploading…' : opponent.logo_file_id ? 'Replace logo' : 'Upload logo'}
          </Button>
          {opponent.logo_file_id ? (
            <Button variant="danger" onClick={() => removeLogo.mutate()} disabled={removeLogo.isPending}>
              Remove logo
            </Button>
          ) : null}
          <Button
            onClick={() => {
              if (opponent.archived || window.confirm(`Archive ${opponent.name}? It leaves the game picker; games already against it keep it.`)) archive.mutate();
            }}
            disabled={archive.isPending}
          >
            {opponent.archived ? 'Restore' : 'Archive'}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

function OpponentForm({ opponent, onDone, onCancel, onError }: { opponent: OpponentRow | null; onDone: () => void; onCancel: () => void; onError: (cause: ApiError) => void }) {
  const [name, setName] = useState(opponent?.name ?? '');
  const [shortName, setShortName] = useState(opponent?.short_name ?? '');
  const [abbreviation, setAbbreviation] = useState(opponent?.abbreviation ?? '');
  const save = useMutation({
    mutationFn: () => {
      const input = { name: name.trim(), short_name: shortName.trim() || null, abbreviation: abbreviation.trim() || null };
      return opponent ? opponentEndpoints.update(opponent.id, input) : opponentEndpoints.create(input);
    },
    onSuccess: onDone,
    onError,
  });
  const prefix = opponent ? `opponent-${opponent.id}` : 'opponent-new';
  return (
    <form
      className="space-y-3 border-b border-line px-5 py-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <div className="flex flex-wrap gap-4">
        <Field label="Name" htmlFor={`${prefix}-name`} hint="In full, as the schedule prints it">
          <input id={`${prefix}-name`} value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} placeholder="Quincy Doggy Paddlers" className={`w-72 ${INPUT}`} />
        </Field>
        <Field label="Short name" htmlFor={`${prefix}-short`} hint="Where there is room for one or two words">
          <input id={`${prefix}-short`} value={shortName} onChange={(e) => setShortName(e.target.value)} maxLength={60} placeholder="Doggy Paddlers" className={`w-56 ${INPUT}`} />
        </Field>
        <Field label="Abbreviation" htmlFor={`${prefix}-abbr`} hint="Up to 8 characters">
          <input id={`${prefix}-abbr`} value={abbreviation} onChange={(e) => setAbbreviation(e.target.value)} maxLength={8} placeholder="QUI" className={`w-24 font-mono ${INPUT}`} />
        </Field>
      </div>
      <div className="flex gap-2">
        <Button type="submit" variant="primary" disabled={save.isPending || !name.trim()}>
          {save.isPending ? 'Saving…' : opponent ? 'Save' : 'Create opponent'}
        </Button>
        <Button type="button" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
